/// <reference path="../../../WebUI/typings/tsd.d.ts" />
import * as React from "react";
import * as ReactDOM from "react-dom";

import * as Dialogs from "../Dialogs/KeyCodes";

export interface DropdownMenuProps extends React.Props<DropdownMenu> {
    onItemSelected?: (selectedIndex: number) => void;
    classNames?: string;
}

interface DropdownMenuState {
    visible: boolean;
    selectedIndex?: number;
}

const MENU_CSS = "dropdown-menu";
const MENU_ITEM_CSS = "dropdown-menu-item";
const HIGLIGHTED_MENU_ITEM_CSS = "dropdown-menu-item-highlight";

export class DropdownMenu extends React.Component<DropdownMenuProps, DropdownMenuState> {
    private $el: JQuery;
    private $anchor: JQuery;
    private children: JSX.Element[];
    private checkForOutsideEvent: (e: MouseEvent) => void;

    static defaultState(): DropdownMenuState {
        return { visible: true, selectedIndex: -1 };
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            visible: false,
            selectedIndex: -1
        };

        this.children = React.Children.toArray(this.props.children) as JSX.Element[];

        this.checkForOutsideEvent = (e) => {
            //see if the event element is within this menu's subtree
            var closest = $(e.target).closest(this.$el);
            if (closest.length == 0) {
                //hide if the event happened outside of this menu's subtree
                this.hide();
            }
        };
    }

    /**
     * Positions the menu below the supplied anchor, and shows the menu.
     */
    public displayBelow($anchor: JQuery, $alignTo?: JQuery): void {
        this.setState(DropdownMenu.defaultState(), () => {
            $alignTo = $alignTo || $anchor;
            var alignOffset = $alignTo.offset();
            var alignHeight = $alignTo.height();
            var alignRight = alignOffset.left + $alignTo.outerWidth(false);
            var menuWidth = this.$el.outerWidth(false);
            var offsetParent = this.$el.offsetParent();
            var parentOffset = offsetParent.offset();
            this.$el.css({ left: (alignRight - menuWidth - parentOffset.left) + 'px', top: (alignOffset.top + alignHeight - parentOffset.top) + 'px' });

            this.increment(1, this.children.length, 0);
            this.$anchor = $anchor;
            this.$anchor.addClass('dropdown-anchor-active');
        });

        //do not attach while on this thread, because we don't want to handle immediately
        setTimeout(() => this.AttachClickOutListeners(), 0);
    }

    private AttachClickOutListeners() {
        document.body.addEventListener('focus', this.checkForOutsideEvent, true);
        document.body.addEventListener('click', this.checkForOutsideEvent);
    }

    private DetachClickOutListeners() {
        document.body.removeEventListener('focus', this.checkForOutsideEvent, true);
        document.body.removeEventListener('click', this.checkForOutsideEvent);
    }

    public hide(): void {
        this.setState({ visible: false });
        
        this.$anchor.removeClass('dropdown-anchor-active');
        this.$anchor.focus();
        this.DetachClickOutListeners();
    }

    private onItemSelected(selectedIndex: number): void {
        var selectedChild = React.Children.toArray(this.props.children)[selectedIndex] as JSX.Element;
        if (selectedChild) {
            var childProps = selectedChild.props as DropdownMenuItemProps;

            if (childProps.notSelectable) return;

            this.hide();

            if (this.props.onItemSelected) {
                this.props.onItemSelected(selectedIndex);
            }

            if (childProps.onSelected) {
                childProps.onSelected();
            }
        }
    }

    private increment(step: number, limit: number, rollover: number) {
        var selectedIndex = this.state.selectedIndex;

        selectedIndex += step;
        if (selectedIndex == limit) {
            selectedIndex = rollover;
        } else if (selectedIndex < 0 || selectedIndex >= this.children.length) {
            selectedIndex = 0;
        }
        var selectedChild = this.children[selectedIndex];
        var selectedProps = selectedChild.props as DropdownMenuItemProps;
        if (selectedProps.notSelectable) {
            this.$el.focus();
            return;
        }

        var newState: DropdownMenuState = { visible: true, selectedIndex: selectedIndex };
        this.setState(newState);
        $(this.$el[0].childNodes[selectedIndex]).focus(); // This will affect narrator
    }

    private highlightItem(selectedIndex: number) {
        var highlightedItem = this.children[selectedIndex];
        if (!highlightedItem) {
            selectedIndex = -1;
        } else {
            var selectedProps = this.children[selectedIndex].props as DropdownMenuItemProps;
            if (selectedProps.notSelectable) {
                selectedIndex = -1;
            }
        }
        var newState: DropdownMenuState = { visible: true, selectedIndex: selectedIndex };
        this.setState(newState);
    }

    private onKeyDown(ev: React.KeyboardEvent<any>): void {
        switch (ev.which) {
            case Dialogs.KeyCodes.Escape:
                this.hide();
                break;
            case Dialogs.KeyCodes.Enter:
                this.onItemSelected(this.state.selectedIndex);
                break;
            case Dialogs.KeyCodes.Tab:
                this.increment(1, this.children.length, 0);
                break;
        }
        ev.preventDefault();
        
        //ev.stopPropagation(); // This will affect narrator
    }

    public render(): JSX.Element {
        var style = {
            display: this.state.visible ? "block" : "none"
        };

        var items: JSX.Element[] = [];

        React.Children.forEach(this.props.children, (child: JSX.Element, index: number) => {

            var oldProps = child.props as DropdownMenuItemProps;
            var newProps: DropdownMenuItemProps = {
                classNames: 'dropdown-menu-item ' + (oldProps.classNames || ''),
                key: index,
                highlighted: (index === this.state.selectedIndex),
                onMouseOver: () => this.highlightItem(index),
                onSelected: () => this.onItemSelected(index)
            };

            items.push(React.cloneElement(child, newProps));
        });

        return (
            <div 
                className={MENU_CSS + ' ' + (this.props.classNames || '') } 
                style={style} 
                tabIndex={0}
                onMouseLeave={()=> this.highlightItem(-1) }
                ref={el => this.$el = $(el) }
                onKeyDown={ev => this.onKeyDown(ev) }>
                {items}
            </div>
        );
    }
}

export interface DropdownMenuItemProps extends React.Props<DropdownMenuItem> {
    highlighted?: boolean;
    onSelected?: () => void;
    onMouseOver?: () => void;
    classNames?: string;
    notSelectable?: boolean;
    ariaLabel?: string;
}

export class DropdownMenuItem extends React.Component<DropdownMenuItemProps, Object> {

    public render(): JSX.Element {
        var classNames = (this.props.classNames || '') + ' ' + MENU_ITEM_CSS;

        if (this.props.highlighted) {
            classNames += ' ' + HIGLIGHTED_MENU_ITEM_CSS;
        }
        return (
            <div className={classNames} onClick={() => this.props.onSelected() } aria-label={this.props.ariaLabel} onMouseOver={() => this.props.onMouseOver() } tabIndex={0} >
                {this.props.children}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./Client/Views/DropdownMenu.tsx